import { OfferImages, OfferItem } from "../../../../../../redux/scanToSlice";
import {
    DISPLAY_KEY_DESKTOP,
    DISPLAY_KEY_MOBILE,
    DISPLAY_TYPE_MOBILE,
    SCAN_TO_DEFAULT_LANGUAGE,
} from "../shared/constants";

// Shape of an offer once it has been translated and resolved for the
// mobile preview. Matches OffresPreviewData["offers"][number] in
// View/types.ts.
export type FlatOffer = {
    id: number;
    title: string;
    description: string;
    tag: string;
    url?: string;
    imageUrl?: string;
};

// What the Redux `state.scanTo.offers` shape looks like once the broadcast
// has been applied. The reducer reshapes `payload.offers` into the
// { Mobile, Desktop } persistence form, optionally preserves the runtime
// view under `previewOffers`, and copies the other broadcast fields
// (displayType, selectedOffersLang, etc.) verbatim — but the typed
// OffersConfig only declares a subset. Type the input precisely here so
// the helper can be unit-tested without leaking `any` to its callers.
export type PersistedOffersData = {
    offers?: OfferImages | OfferItem[];
    previewOffers?: FlatOffer[];
    displayType?: "mobile" | "desktop" | string;
    selectedOffersLang?: string;
    selectedLanguage?: string;
};

function pickLang(value: unknown, lang: string): string {
    if (typeof value === "string") return value;
    if (value && typeof value === "object") {
        const v = (value as Record<string, unknown>)[lang];
        return typeof v === "string" ? v : "";
    }
    return "";
}

function resolveImageUrl(offer: OfferItem): string {
    if (offer.imageUrl) return offer.imageUrl;
    if (typeof offer.image === "string" && offer.image.startsWith("http")) {
        return offer.image;
    }
    return "";
}

// Rebuild the flat translated offers array the preview expects from the
// raw value persisted in Redux. Resolution order:
//   1. raw.previewOffers — the broadcast's runtime view (already flat,
//      translated, with resolved imageUrl including blob URLs). Used
//      when present so other rubrics see the user's in-flight edits
//      from Offres including newly uploaded images.
//   2. raw.offers as a flat array — direct passthrough (the broadcast
//      sometimes lands as an array before the reducer reshapes it).
//   3. raw.offers as { Mobile, Desktop } — rebuild from persistence by
//      picking the active displayType bucket, filtering to active,
//      sorting by order, and translating per-language fields with the
//      persisted selectedOffersLang.
export function flattenOffersForPreview(
    raw: PersistedOffersData | null | undefined
): FlatOffer[] {
    if (!raw) return [];
    if (Array.isArray(raw.previewOffers)) return raw.previewOffers;
    const rawOffers = raw.offers;
    if (Array.isArray(rawOffers)) {
        // Already flat — assume it was broadcast directly without reducer
        // reshaping; cast through unknown to satisfy the FlatOffer shape.
        return (rawOffers as unknown) as FlatOffer[];
    }
    const displayKey =
        raw.displayType === DISPLAY_TYPE_MOBILE
            ? DISPLAY_KEY_MOBILE
            : DISPLAY_KEY_DESKTOP;
    // `[]` is truthy in JS, so `bucket || Desktop || []` would never fall
    // through when the requested bucket is present but empty. Pick the
    // first non-empty bucket explicitly.
    const requested = rawOffers?.[displayKey];
    const desktop = rawOffers?.[DISPLAY_KEY_DESKTOP];
    const source: OfferItem[] =
        requested && requested.length > 0
            ? requested
            : desktop && desktop.length > 0
            ? desktop
            : [];
    const lang =
        raw.selectedOffersLang ||
        raw.selectedLanguage ||
        SCAN_TO_DEFAULT_LANGUAGE;
    return source
        .filter((o) => o.active !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((o) => ({
            id: o.id,
            title: pickLang(o.title, lang),
            description: pickLang(o.description, lang),
            tag: o.tag,
            url: o.url,
            imageUrl: resolveImageUrl(o),
        }));
}
